/**
 * グローバルエラーバウンダリ (Phase 3).
 *
 * React のレンダリングエラーをキャッチして 500 的な画面を表示する。
 * Figma: #20 (500エラー)
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../lib/sentry.js';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
    captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <p className="text-7xl font-bold text-gray-200">500</p>
            <h1 className="mt-4 text-xl font-bold text-gray-900">予期しないエラーが発生しました</h1>
            <p className="mt-2 text-sm text-gray-500">
              問題が解決しない場合は、ページを再読み込みしてください。
            </p>
            {this.state.error && (
              <details className="mx-auto mt-4 max-w-md text-left">
                <summary className="cursor-pointer text-xs text-gray-400">エラー詳細</summary>
                <pre className="mt-2 overflow-auto rounded-md bg-gray-100 p-3 text-xs text-gray-700">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 inline-block rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              再読み込み
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
